import { useState, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';
import { useAddFromLms } from '@/features/people/use-people';

import { registrationError } from '../presentation';
import { useSetOwner } from '../use-document-registration';
import { OwnerField, type OwnerSelection } from './OwnerField';

// Asignar, cambiar o quitar el dueño de proceso desde la ficha (G3-3). Misma elección que
// en el alta: una persona de la organización; una del LMS se agrega al QMS al guardar.

export function OwnerModal({
  entryId,
  code,
  hasOwner,
  onClose,
}: {
  entryId: string;
  code: string;
  hasOwner: boolean;
  onClose: () => void;
}): ReactElement {
  const setOwner = useSetOwner(entryId);
  const addFromLms = useAddFromLms();
  const { showToast } = useToast();
  const [chosen, setChosen] = useState<OwnerSelection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function apply(next: OwnerSelection | null): Promise<void> {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const userId =
        next === null
          ? null
          : next.kind === 'qms'
            ? next.userId
            : await addFromLms.mutateAsync({ authSubject: next.authSubject });
      await setOwner.mutateAsync(userId);
      showToast(
        next === null
          ? `${code} quedó sin dueño de proceso`
          : `Dueño de proceso de ${code}: ${next.name}`,
      );
      onClose();
    } catch (err) {
      setError(registrationError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      open
      title="Dueño de proceso"
      busy={busy}
      onClose={onClose}
      footer={
        <>
          {hasOwner && (
            <button
              type="button"
              className="btn danger sm"
              disabled={busy}
              onClick={() => void apply(null)}
            >
              Quitar dueño
            </button>
          )}
          <button type="button" className="btn mut sm" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm"
            disabled={chosen === null || busy}
            onClick={() => void apply(chosen)}
          >
            {busy ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <p className="modal-body">
        El dueño de proceso de {code} recibe el aviso de su revisión periódica.
      </p>
      <div className="form-grid">
        <OwnerField value={chosen} onChange={setChosen} />
        {error && (
          <p className="fld-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
